/**
 * Breakdown: the period's runs totalled per process, model, person, day or
 * (in a year) month, under the filters it shares with Runs. "By" picks the
 * dimension; every column sorts (cost, highest first, by default) and the
 * list pages on the server. A group's share is of the cost of every matching
 * group. A group's name opens Runs with that group added to the filters.
 */

import { ChevronDown, Gauge, Layers } from 'lucide-react';

import { useRunGroups } from '@/api/queries';
import type { Period, RunGroup, RunGroupBy, RunGroupList, Usage } from '@/api/types';
import { formatCompact, formatInt, formatPct, formatUsd, monthLabel } from '@/ui/format';
import { Pagination } from '@/ui/Pagination';
import { SortHeader } from '@/ui/SortHeader';
import { LoadFailure } from '@/ui/StateCard';

import { RunFilterBar, isFiltered, useMatchingParams, type RunFilters } from './RunFilterBar';
import { SkeletonRows } from './SkeletonRows';
import { formatDuration, sortPatch } from './usageLists';
import { GROUP_BY, GroupName, formatDay, groupInfo, isYear, periodLabel } from './usageUi';

export interface BreakdownView {
  by: RunGroupBy;
  sort: string;
  order: 'asc' | 'desc';
  page: number;
  page_size: number;
}

export const DEFAULT_BREAKDOWN: BreakdownView = { by: 'process', sort: 'cost_usd', order: 'desc', page: 1, page_size: 50 };

const COLUMNS = 7;

const GROUP_LABEL: Partial<Record<RunGroupBy, (g: RunGroup) => string>> = {
  day: (g) => formatDay(g.key),
  month: (g) => monthLabel(g.key),
};

function groupLabel(by: RunGroupBy, g: RunGroup): string {
  const label = GROUP_LABEL[by];
  return label ? label(g) : g.label;
}

export function BreakdownTab({
  period,
  data,
  filters,
  onFilters,
  view,
  onView,
  onDrill,
}: {
  period: Period;
  data: Usage;
  filters: RunFilters;
  onFilters: (patch: Partial<RunFilters>) => void;
  view: BreakdownView;
  onView: (patch: Partial<BreakdownView>) => void;
  onDrill: (by: RunGroupBy, key: string) => void;
}) {
  const matching = useMatchingParams(period, filters);
  const groups = useRunGroups({ ...matching, ...view });
  const list = groups.data;

  return (
    <>
      <RunFilterBar
        period={period}
        data={data}
        filters={filters}
        onChange={onFilters}
        lead={<ByControl period={period} by={view.by} onView={onView} />}
      />

      {groups.error ? (
        <LoadFailure error={groups.error} onRetry={() => void groups.refetch()} />
      ) : (
        <>
          <BreakdownTable
            period={period}
            list={list}
            pending={groups.isPending}
            filtered={isFiltered(filters)}
            view={view}
            onView={onView}
            onDrill={onDrill}
          />
          {list && list.total > 0 && (
            <Pagination
              page={view.page}
              pageSize={view.page_size}
              total={list.total}
              noun={groupInfo(view.by).noun}
              onPage={(page) => onView({ page })}
              onPageSize={(page_size) => onView({ page_size, page: 1 })}
            />
          )}
        </>
      )}
    </>
  );
}

function ByControl({ period, by, onView }: { period: Period; by: RunGroupBy; onView: (patch: Partial<BreakdownView>) => void }) {
  return (
    <span className="select-wrap has-lead">
      <Layers className="select-lead" aria-hidden="true" />
      <select
        className="toolbar-select"
        aria-label="Break down by"
        value={by}
        onChange={(e) => onView({ by: e.target.value as RunGroupBy, page: 1 })}
      >
        {GROUP_BY.filter((g) => g.key !== 'month' || isYear(period)).map((g) => (
          <option key={g.key} value={g.key}>By {g.label.toLowerCase()}</option>
        ))}
      </select>
      <ChevronDown aria-hidden="true" />
    </span>
  );
}

function BreakdownTable({
  period,
  list,
  pending,
  filtered,
  view,
  onView,
  onDrill,
}: {
  period: Period;
  list: RunGroupList | undefined;
  pending: boolean;
  filtered: boolean;
  view: BreakdownView;
  onView: (patch: Partial<BreakdownView>) => void;
  onDrill: (by: RunGroupBy, key: string) => void;
}) {
  const rows = list?.groups ?? [];
  const totalCost = Number(list?.total_cost_usd ?? 0);
  const onSort = (key: string) => onView(sortPatch(view, key, (k) => k === 'label'));
  const sortProps = { sort: view.sort, order: view.order, onSort };

  return (
    <div className="table-scroll">
      <table className="data zebra compact">
        <thead>
          <tr>
            <SortHeader label={groupInfo(view.by).column} sortKey="label" {...sortProps} />
            <SortHeader label="Runs" sortKey="runs" className="num" {...sortProps} />
            <SortHeader label="Failed" sortKey="failed" className="num" {...sortProps} />
            <SortHeader label="Tokens" sortKey="tokens" className="num" {...sortProps} />
            <SortHeader label="Avg duration" sortKey="avg_latency_ms" className="num" {...sortProps} />
            <SortHeader label="Cost" sortKey="cost_usd" className="num" {...sortProps} />
            <th scope="col" className="share-col">Share of cost</th>
          </tr>
        </thead>
        <tbody>
          {pending ? (
            <SkeletonRows columns={COLUMNS} />
          ) : rows.length === 0 ? (
            <EmptyRow period={period} filtered={filtered} />
          ) : (
            rows.map((g) => (
              <GroupRow key={g.key} group={g} by={view.by} totalCost={totalCost} onDrill={() => onDrill(view.by, g.key)} />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function EmptyRow({ period, filtered }: { period: Period; filtered: boolean }) {
  return (
    <tr>
      <td colSpan={COLUMNS}>
        <div className="empty">
          <div className="e-icon"><Gauge aria-hidden="true" /></div>
          <h3>{filtered ? 'No runs match' : `No AI calls in ${periodLabel(period)}`}</h3>
          <p>{filtered ? `Clear the filters to see the rest of ${periodLabel(period)}.` : 'Totals appear here once modules call the AI layer.'}</p>
        </div>
      </td>
    </tr>
  );
}

function GroupRow({ group: g, by, totalCost, onDrill }: { group: RunGroup; by: RunGroupBy; totalCost: number; onDrill: () => void }) {
  const share = totalCost > 0 ? (Number(g.cost_usd) / totalCost) * 100 : 0;
  return (
    <tr>
      <td>
        <GroupName label={groupLabel(by, g)} drillable={g.key !== '—'} onClick={onDrill} />
      </td>
      <td className="num">{formatInt(g.runs)}</td>
      <td className={`num${g.failed ? '' : ' hint'}`}>{formatInt(g.failed)}</td>
      <td className="num">{formatCompact(g.tokens_input + g.tokens_output)}</td>
      <td className="num">{formatDuration(g.avg_latency_ms)}</td>
      <td className="num">{formatUsd(g.cost_usd)}</td>
      <td className="share-col">
        <span className="share-cell">
          <span className="bar-track" aria-hidden="true">
            <span className="bar-fill" style={{ width: `${share}%` }} />
          </span>
          <span className="share-value">{formatPct(share)}</span>
        </span>
      </td>
    </tr>
  );
}
